import apiClient from './client'
import type { Operator } from '@/types'

export interface CreateOperatorData {
  fullname: string
  extension: string
  project: string
  location?: string
  sip_password?: string
}

export interface UpdateOperatorData {
  fullname?: string
  project?: string
  location?: string
  status?: string
  sip_password?: string
  expected_input_device?: string | null
  expected_output_device?: string | null
  phone_number?: string | null
  mic_volume_min?: number | null
  mic_volume_max?: number | null
  output_volume_min?: number | null
  output_volume_max?: number | null
}

interface OperatorListResponse {
  total: number
  operators: Operator[]
}

export async function getOperators(): Promise<Operator[]> {
  const response = await apiClient.get<OperatorListResponse>('/operators/')
  return response.data.operators
}

export async function getOperator(id: number): Promise<Operator> {
  const response = await apiClient.get<Operator>(`/operators/${id}`)
  return response.data
}

export async function getOperatorByMachineId(machineId: string): Promise<Operator> {
  const response = await apiClient.get<Operator>(`/operators/by-machine/${machineId}`)
  return response.data
}

export async function createOperator(data: CreateOperatorData): Promise<Operator> {
  const response = await apiClient.post<Operator>('/operators/', data)
  return response.data
}

export async function updateOperator(id: number, data: UpdateOperatorData): Promise<Operator> {
  const response = await apiClient.put<Operator>(`/operators/${id}`, data)
  return response.data
}

export async function updateOperatorAudioConfig(
  id: number,
  config: { expected_input_device?: string | null; expected_output_device?: string | null }
): Promise<Operator> {
  const response = await apiClient.put<Operator>(`/operators/${id}`, config)
  return response.data
}

export async function deleteOperator(id: number): Promise<void> {
  await apiClient.delete(`/operators/${id}`)
}

export interface GenerateInstallTokenPayload {
  use_vpn_sip?: boolean
  tailscale_authkey?: string
  tailscale_hostname?: string
}

export async function generateInstallToken(
  id: number,
  payload: GenerateInstallTokenPayload = {}
): Promise<{ token: string; installation_url: string; expires_at: string }> {
  const response = await apiClient.post(`/operators/${id}/installation-token`, payload)
  return response.data
}

// Sync from VoIP types
export interface SyncFromVoipRequest {
  project: string
  location?: string
  dry_run?: boolean
}

export interface SyncResultItem {
  extension: string
  action: 'created' | 'exists' | 'skipped'
  operator_id?: number
  fullname?: string
  reason?: string
}

export interface SyncFromVoipResponse {
  total_extensions: number
  created: number
  already_exists: number
  skipped: number
  details: SyncResultItem[]
}

export async function syncFromVoip(data: SyncFromVoipRequest): Promise<SyncFromVoipResponse> {
  const response = await apiClient.post<SyncFromVoipResponse>('/operators/sync-from-voip', data)
  return response.data
}
